import React from "react";
import styled, { keyframes } from "styled-components";
import { Form, Button, Spinner, Container } from "react-bootstrap";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddIcon from "@material-ui/icons/Add";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import TimerIcon from "@material-ui/icons/Timer";

/* ─── Design tokens ──────────────────────────────────────────── */
const BRAND = "#0c8de3";
const BRAND_DARK = "#0c234c";
const BRAND_LIGHT = "rgba(12,141,227,0.08)";

const RUBRO_COLORS = {
  SUELDOS: { bg: "#fef3c7", text: "#92400e", dot: "#f59e0b" },
  LOCAL:   { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  FONDO:   { bg: "#d1fae5", text: "#065f46", dot: "#10b981" },
  APOYO:   { bg: "#ede9fe", text: "#4c1d95", dot: "#8b5cf6" },
  OTROS:   { bg: "#f3f4f6", text: "#374151", dot: "#6b7280" },
};

/* ─── Keyframes ─────────────────────────────────────────────── */
const pulse = keyframes`
  0%   { opacity: 1; }
  50%  { opacity: 0.7; }
  100% { opacity: 1; }
`;

/* ─── Styled components ─────────────────────────────────────── */
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #f8fafc;
  padding-bottom: 80px; /* space for sticky footer */
`;

const HeroCard = styled.div`
  background: linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%);
  border-radius: 16px;
  padding: 20px 24px 18px;
  margin-bottom: 20px;
  color: #fff;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    right: -20px;
    top: -20px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.07);
  }
`;

const HeroLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.75;
  margin-bottom: 2px;
`;

const HeroAmount = styled.div`
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  margin-bottom: 4px;
`;

const HeroSub = styled.div`
  font-size: 0.78rem;
  opacity: 0.75;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const HeroDivider = styled.div`
  width: 1px;
  height: 14px;
  background: rgba(255, 255, 255, 0.3);
`;

const SectionTitle = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 10px;
  margin-top: 4px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const JornadaRow = styled.div`
  display: flex;
  gap: 12px;
`;

const JornadaField = styled.div`
  flex: 1;
  min-width: 0;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 4px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${BRAND_DARK};
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &:focus {
    border-color: ${BRAND};
    background: #fff;
  }
`;

const AddRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledSelect = styled.select`
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.88rem;
  font-weight: 500;
  color: ${BRAND_DARK};
  background: #f8fafc;
  outline: none;
  appearance: none;
  transition: border-color 0.15s;
  cursor: pointer;

  &:focus {
    border-color: ${BRAND};
    background: #fff;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 14px;
  background: ${BRAND};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, transform 0.1s;
  flex-shrink: 0;

  &:active {
    transform: scale(0.97);
    background: #0974c4;
  }
`;

const GastosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GastoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 10px;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const RubroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px;
  border-radius: 6px;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
  background: ${(p) => p.bg || "#f3f4f6"};
  color: ${(p) => p.textColor || "#374151"};

  &::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.dot || "#6b7280"};
    flex-shrink: 0;
  }
`;

const InlineInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 5px 8px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: ${BRAND_DARK};
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${BRAND};
    background: #fff;
  }

  &::placeholder {
    color: #cbd5e1;
  }
`;

const ValueWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const CurrencyPrefix = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
  flex-shrink: 0;
`;

const ValueInput = styled.input`
  width: 84px;
  padding: 5px 6px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${BRAND_DARK};
  background: #f8fafc;
  text-align: right;
  outline: none;
  transition: border-color 0.15s;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &:focus {
    border-color: ${BRAND};
    background: #fff;
  }
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #cbd5e1;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;

  /* 44×44 touch target via padding */
  padding: 6px;

  &:hover,
  &:active {
    background: #fee2e2;
    color: #ef4444;
  }
`;

const StickyFooter = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  z-index: 10;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
`;

const TotalLabel = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const TotalAmount = styled.span`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${BRAND_DARK};
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: ${BRAND};
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, opacity 0.15s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:not(:disabled):active {
    background: #0974c4;
  }
`;

/* ─── Component ─────────────────────────────────────────────── */
export default function GastosFijosForm({
  gastosFijos,
  rubros,
  rubroId,
  setRubroId,
  loading,
  onChangeProperty,
  onChangeItems,
  onAddItem,
  onDeleteItem,
  onSave,
  isSaving,
  getCostoHora,
  getCostoMinuto,
  getTotalGastos,
  showSubmit = true,
}) {
  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner animation="border" style={{ color: BRAND }} />
      </div>
    );

  const costoMinuto = getCostoMinuto();
  const costoHora = getCostoHora();
  const dias = gastosFijos.dias || 0;
  const horas = gastosFijos.horas || 0;
  const total = getTotalGastos();

  return (
    <PageWrapper>
      <Container fluid="sm" className="py-3 px-3">

        {/* ── Hero metric ────────────────────────────────── */}
        <HeroCard>
          <HeroLabel>
            <TimerIcon style={{ fontSize: 12, marginRight: 4, verticalAlign: "middle" }} />
            Costo por minuto de silla
          </HeroLabel>
          <HeroAmount>
            S/ {costoMinuto > 0 ? costoMinuto.toFixed(2) : "—"}
          </HeroAmount>
          <HeroSub>
            <span>
              <AccessTimeIcon style={{ fontSize: 12, marginRight: 3, verticalAlign: "middle" }} />
              S/ {costoHora > 0 ? costoHora.toFixed(2) : "—"} / hora
            </span>
            <HeroDivider />
            <span>{dias}d × {horas}h al mes</span>
          </HeroSub>
        </HeroCard>

        {/* ── Jornada mensual ────────────────────────────── */}
        <SectionTitle>Jornada mensual</SectionTitle>
        <Card>
          <JornadaRow>
            <JornadaField>
              <FieldLabel htmlFor="dias">Días / mes</FieldLabel>
              <StyledInput
                id="dias"
                type="number"
                name="dias"
                value={gastosFijos.dias}
                onChange={onChangeProperty}
                min={0}
                step={1}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </JornadaField>
            <JornadaField>
              <FieldLabel htmlFor="horas">Horas / día</FieldLabel>
              <StyledInput
                id="horas"
                type="number"
                name="horas"
                value={gastosFijos.horas}
                onChange={onChangeProperty}
                min={0}
                step={0.5}
                inputMode="numeric"
              />
            </JornadaField>
          </JornadaRow>
        </Card>

        {/* ── Gastos del mes ─────────────────────────────── */}
        <SectionTitle>Gastos del mes</SectionTitle>
        <Card>
          {/* Add row */}
          <AddRow className="mb-3">
            <StyledSelect
              value={rubroId}
              onChange={(e) => setRubroId(e.target.value)}
            >
              <option hidden value="">Rubro…</option>
              {rubros.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </StyledSelect>
            <AddButton type="button" onClick={onAddItem}>
              <AddIcon style={{ fontSize: 16 }} />
              Agregar
            </AddButton>
          </AddRow>

          {/* Items list */}
          <GastosList>
            {gastosFijos.items.length === 0 && (
              <div style={{ textAlign: "center", color: "#94a3b8", fontSize: "0.82rem", padding: "12px 0" }}>
                Sin gastos registrados. Agrega un rubro.
              </div>
            )}
            {gastosFijos.items.map((item, index) => {
              const colors = RUBRO_COLORS[item.rubro] || RUBRO_COLORS.OTROS;
              return (
                <GastoRow key={index}>
                  <RubroBadge bg={colors.bg} textColor={colors.text} dot={colors.dot}>
                    {item.rubro}
                  </RubroBadge>
                  <InlineInput
                    name="concepto"
                    placeholder="Concepto"
                    type="text"
                    autoComplete="off"
                    value={item.concepto}
                    onChange={(e) => onChangeItems(index, e)}
                  />
                  <ValueWrapper>
                    <CurrencyPrefix>S/</CurrencyPrefix>
                    <ValueInput
                      name="valor"
                      type="number"
                      inputMode="decimal"
                      autoComplete="off"
                      value={item.valor}
                      onChange={(e) => onChangeItems(index, e)}
                      min={0}
                      placeholder="0"
                    />
                  </ValueWrapper>
                  <DeleteButton
                    type="button"
                    onClick={() => onDeleteItem(index)}
                    title="Eliminar gasto"
                  >
                    <DeleteOutlineIcon style={{ fontSize: 18 }} />
                  </DeleteButton>
                </GastoRow>
              );
            })}
          </GastosList>
        </Card>

      </Container>

      {/* ── Sticky footer ──────────────────────────────────── */}
      <StickyFooter>
        <div>
          <TotalLabel>Total mensual</TotalLabel>
          <br />
          <TotalAmount>S/ {Number(total).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TotalAmount>
        </div>
        {showSubmit && (
          <SaveButton
            type="button"
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" />
                Guardando…
              </>
            ) : (
              "Guardar"
            )}
          </SaveButton>
        )}
      </StickyFooter>
    </PageWrapper>
  );
}